/**
 * AuditLogEntry — Lese-/Schreib-Audit pro Datenzugriff.
 *
 * Aufbewahrung 10 Jahre (Beweislast Stellungnahme). before/after sind als
 * `unknown` (JSON) typisiert — die Entry ist generisch über alle Tabellen.
 * Die DB-Trigger (siehe `supabase/migrations/0002_audit_triggers.sql`)
 * füllen das Feld; client-seitig wird hier nur gelesen.
 */
import { z } from 'zod';
import { AuditLogEntryIdSchema, CaseIdSchema, UserIdSchema } from './ids.js';

export const AuditActionSchema = z.enum([
  'select',
  'insert',
  'update',
  'delete',
  'soft_delete',
  'export',
  'sign_statement',
  'login',
  'logout',
]);
export type AuditAction = z.infer<typeof AuditActionSchema>;

export const AuditEntityTypeSchema = z.enum([
  'case',
  'dimension_data',
  'financial_dataset',
  'score',
  'statement',
  'user',
  'auth',
]);
export type AuditEntityType = z.infer<typeof AuditEntityTypeSchema>;

export const AuditLogEntrySchema = z.object({
  id: AuditLogEntryIdSchema,
  /** Wer — auth-User. `null` nur für system-initiierte Aktionen (Cron). */
  actorUserId: UserIdSchema.nullable(),
  /** Was — Aktion */
  action: AuditActionSchema,
  /** Worauf — Entitätstyp */
  entityType: AuditEntityTypeSchema,
  /** Worauf — primärer Entity-Identifier (uuid string, nicht gebrandet, weil polymorph) */
  entityId: z.string().min(1).max(64),
  /** Optionaler Case-Kontext für schnellere Filterung */
  caseId: CaseIdSchema.nullable(),
  /** Vollständiger Vorzustand (bei update/delete) */
  before: z.unknown().nullable(),
  /** Vollständiger Nachzustand (bei insert/update) */
  after: z.unknown().nullable(),
  /** IP-Adresse (gehasht/anonymisiert oder vollständig — DSGVO-Entscheidung) */
  ipAddress: z.string().max(45).nullable().optional(),
  /** User-Agent — kein PII, aber forensisch nützlich */
  userAgent: z.string().max(500).nullable().optional(),
  occurredAt: z.coerce.date(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;
